/**
 * Working with a case: the four write routes and how their failures read.
 *
 * Every write goes to the app database only (a decision on a line, the status of a
 * case, who works on it, which customer it is for). Nothing is sent to a customer and
 * nothing is written to D365.
 *
 * Each request carries `expected_version`, the version of the work row the page was
 * showing (0 when the case has no work row yet). The server answers 409 with the
 * current row when somebody else changed the case in between.
 *
 * Kept free of React so the request shapes and the error wording can be tested.
 */

import type { QueryClient } from "@tanstack/react-query";

import { type ApiError, client } from "./client";
import type {
  CaseLookAgainRequest,
  LineOverrideRequest,
  LineQuantitiesRequest,
  LineQuantitySkip,
  ProposalRejectRequest,
} from "./generated";
import type {
  CaseAssignRequest,
  CaseCustomerRequest,
  CaseStatusRequest,
  CaseWork,
  CaseWorkConflict,
  CaseWorkResult,
  LineAddedRemoveRequest,
  LineAddRequest,
  LineDecision,
  LineDecisionRequest,
  WorkStatus,
} from "./live-types";

/** The version to send with a write: the shown work row's, or 0 without one. */
export function expectedVersion(work: Pick<CaseWork, "version"> | null | undefined): number {
  const version = work?.version;
  return typeof version === "number" && Number.isFinite(version) && version > 0 ? version : 0;
}

async function postWork(url: string, caseId: string, body: unknown, lineIndex?: number) {
  const { data } = await client.post<{ 200: CaseWorkResult }, unknown, true>({
    url,
    path:
      lineIndex === undefined ? { case_id: caseId } : { case_id: caseId, line_index: lineIndex },
    body,
    headers: { "Content-Type": "application/json" },
    throwOnError: true,
  });
  return data;
}

/** Confirm, pick, mark "no item" or undo on one draft line. */
export function postLineDecision(caseId: string, lineIndex: number, body: LineDecisionRequest) {
  return postWork("/cases/{case_id}/lines/{line_index}/decision", caseId, body, lineIndex);
}

/** Correct an engine line: reviewed quantity / unit, take it out, or undo (F08). */
export function postLineOverride(caseId: string, lineIndex: number, body: LineOverrideRequest) {
  return postWork("/cases/{case_id}/lines/{line_index}/override", caseId, body, lineIndex);
}

/** The answer of the quantity write on several lines: the work row and what was written. */
export interface LineQuantitiesAnswer {
  work: CaseWork;
  work_status: WorkStatus;
  /** Line keys written, in line order. */
  changed?: string[];
  skipped?: LineQuantitySkip[];
}

/**
 * One reviewed quantity on several lines, or the quantities taken off again (`kind:
 * "restore"`): one write, one version step (`POST /cases/{id}/lines/quantity`).
 */
export async function postLineQuantities(caseId: string, body: LineQuantitiesRequest) {
  const { data } = await client.post<{ 200: LineQuantitiesAnswer }, unknown, true>({
    url: "/cases/{case_id}/lines/quantity",
    path: { case_id: caseId },
    body,
    headers: { "Content-Type": "application/json" },
    throwOnError: true,
  });
  return data;
}

/** Add a line the engine missed, or change one sales added (F08). */
export function postLineAdd(caseId: string, body: LineAddRequest) {
  return postWork("/cases/{case_id}/lines/add", caseId, body);
}

/** Delete a line sales added (F08). */
export function postAddedLineRemove(caseId: string, body: LineAddedRemoveRequest) {
  return postWork("/cases/{case_id}/lines/added/remove", caseId, body);
}

/** Mark done, not for us, waiting for customer, or reopen. */
export function postCaseStatus(caseId: string, body: CaseStatusRequest) {
  return postWork("/cases/{case_id}/status", caseId, body);
}

/** Take, take over or release a case. */
export function postCaseAssign(caseId: string, body: CaseAssignRequest) {
  return postWork("/cases/{case_id}/assign", caseId, body);
}

/** Confirm the customer of a case, or clear the rep's choice with a null account. */
export function postCaseCustomer(caseId: string, body: CaseCustomerRequest) {
  return postWork("/cases/{case_id}/customer", caseId, body);
}

/** Ask the deeper search to look at the case again, or at one line (`line_key`). */
export function postLookAgain(caseId: string, body: CaseLookAgainRequest) {
  return postWork("/cases/{case_id}/investigation/look-again", caseId, body);
}

/** Reject one proposal of the deeper search ("Not this one"). */
export function postRejectProposal(caseId: string, body: ProposalRejectRequest) {
  return postWork("/cases/{case_id}/investigation/reject", caseId, body);
}

// ─── names and times ────────────────────────────────────────────────────────

/**
 * Authors that are not a person, shown by a fixed name instead of a name read from the
 * string. Must agree with the backend's `display_name()` (store/discussions.py).
 */
export const FIXED_AUTHOR_NAMES: Readonly<Record<string, string>> = {
  // The seeded topics of the discussions register (migration 0027).
  "pre-invite-review": "I2Q review, 12 Oct 2026",
  // The seeded business decisions (migration 0031, 15 Oct 2026).
  "i2q-business-decisions": "I2Q project, 15 Oct 2026",
};

/** "anna.jensen@norican.com" reads as "Anna Jensen"; anything else passes through. */
export function personName(email: string | null | undefined): string {
  const raw = (email ?? "").trim();
  if (raw === "") {
    return "";
  }
  const fixed = FIXED_AUTHOR_NAMES[raw];
  if (fixed !== undefined) {
    return fixed;
  }
  const local = raw.includes("@") ? raw.slice(0, raw.indexOf("@")) : raw;
  const words = local.split(/[._-]+/).filter(Boolean);
  if (!raw.includes("@") || words.length === 0) {
    return raw;
  }
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
}

function clock(iso: string | null | undefined): string | null {
  if (!iso) {
    return null;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

/** Who last touched a work row and when: the newest of status, assignment and customer. */
export function lastChangeOf(work: CaseWork | null | undefined): {
  by: string | null;
  at: string | null;
} {
  const changes = [
    { by: work?.status_by ?? null, at: work?.status_at ?? null },
    { by: work?.assignee ?? null, at: work?.assigned_at ?? null },
    { by: work?.customer_by ?? null, at: work?.customer_at ?? null },
  ].filter((change) => change.at && !Number.isNaN(new Date(change.at).getTime()));
  changes.sort((a, b) => new Date(b.at as string).getTime() - new Date(a.at as string).getTime());
  return changes[0] ?? { by: null, at: null };
}

/** The conflict toast: "This case was changed by {name} at {hh:mm}. The page was reloaded." */
export function conflictMessage(work: CaseWork | null | undefined, now: Date = new Date()): string {
  const change = lastChangeOf(work);
  const name = personName(change.by) || "a colleague";
  const time = clock(change.at) ?? clock(now.toISOString());
  return `This case was changed by ${name} at ${time}. The page was reloaded.`;
}

/** Shown when the engine evaluated the case again while the page was open. */
export const LINE_CHANGED_MESSAGE =
  "This line was changed by a new evaluation of the mail. The page was reloaded.";

/** HTTP 409 `look_again_pending`: an earlier "Look again" request still runs. */
export const LOOK_AGAIN_PENDING_MESSAGE =
  "The deeper search already looks at this case. The result comes on this page.";

/** HTTP 503 on a line decision or a catalogue search. */
export const CATALOGUE_DOWN_MESSAGE =
  "The item catalogue is not reachable right now, nothing was saved.";

/** HTTP 503 on a customer confirmation or a customer search. */
export const CUSTOMERS_DOWN_MESSAGE =
  "The customer list is not reachable right now, nothing was saved.";

/**
 * HTTP 503 on a quantity save. The write reads no catalogue and no customer list, so the
 * catalogue words were wrong for it (review of the ease round, 13 Oct 2026).
 */
export const I2Q_DOWN_MESSAGE = "I2Q is not reachable right now, nothing was saved.";

const GENERIC_MESSAGE = "This could not be saved. Try again in a moment.";

/** HTTP 403 on reopening a case a colleague closed (stage 3). */
export const REOPEN_NEEDS_MANAGER_MESSAGE =
  "Only a manager can reopen a case closed by a colleague.";

const FORBIDDEN_MESSAGE = "Your role does not allow this.";

/** Which control a write belongs to; decides the wording of a 503. */
export type WorkTarget = "line" | "case" | "customer" | "quantity";

/** The sentence of a 503, by the control the write belongs to. */
function downMessage(target: WorkTarget): string {
  if (target === "customer") {
    return CUSTOMERS_DOWN_MESSAGE;
  }
  return target === "quantity" ? I2Q_DOWN_MESSAGE : CATALOGUE_DOWN_MESSAGE;
}

/** What a failed write means for the page. */
export type WorkFailure =
  /** Somebody else changed the case: toast, then reload the case. */
  | { kind: "conflict"; message: string; work: CaseWork | null }
  /** The server refused the value: its sentence goes next to the control. */
  | { kind: "rejected"; message: string }
  /** The master data is not reachable: nothing was saved. */
  | { kind: "unreachable"; message: string }
  /** The role of the session does not allow it: nothing was saved. */
  | { kind: "forbidden"; message: string }
  | { kind: "failed"; message: string };

/** FastAPI's `detail`: a sentence, or a list of validation entries with a `msg`. */
export function detailText(body: unknown): string | null {
  const detail = (body as { detail?: unknown } | null)?.detail;
  if (typeof detail === "string" && detail.trim() !== "") {
    return detail.trim();
  }
  if (Array.isArray(detail)) {
    const messages = detail
      .map((entry) => (entry as { msg?: unknown } | null)?.msg)
      .filter((msg): msg is string => typeof msg === "string" && msg.trim() !== "")
      // Pydantic prefixes a validator's own sentence with "Value error, ".
      .map((msg) => msg.replace(/^Value error, /, ""));
    return messages.length > 0 ? messages.join(" ") : null;
  }
  return null;
}

/** Read a thrown `ApiError` from a case write route. */
export function workFailure(
  error: unknown,
  target: WorkTarget = "case",
  now: Date = new Date(),
): WorkFailure {
  const { status, body } = (error ?? {}) as Partial<ApiError>;
  if (status === 409) {
    const detail = (body as Partial<CaseWorkConflict> | null)?.detail;
    const work = detail && typeof detail === "object" && detail.work ? detail.work : null;
    const message =
      detail?.code === "line_changed"
        ? LINE_CHANGED_MESSAGE
        : detail?.code === "look_again_pending"
          ? LOOK_AGAIN_PENDING_MESSAGE
          : conflictMessage(work, now);
    return { kind: "conflict", work, message };
  }
  if (status === 403) {
    return {
      kind: "forbidden",
      message:
        detailText(body) === "reopen_needs_manager"
          ? REOPEN_NEEDS_MANAGER_MESSAGE
          : FORBIDDEN_MESSAGE,
    };
  }
  if (status === 422) {
    return { kind: "rejected", message: detailText(body) ?? "The server did not accept this." };
  }
  if (status === 503) {
    return { kind: "unreachable", message: downMessage(target) };
  }
  return { kind: "failed", message: GENERIC_MESSAGE };
}

/** The sentence for next to the control, or null for a conflict (that one is a toast). */
export function workFailureText(error: unknown, target: WorkTarget = "case"): string | null {
  if (error == null) {
    return null;
  }
  const failure = workFailure(error, target);
  return failure.kind === "conflict" ? null : failure.message;
}

/**
 * What a failed approve or retract of a learned correction reads as. Every user who can
 * see a correction may approve or retract it, so no role sentence applies; a 403 from
 * an unexpected policy still reads as a refusal.
 */
export function learnedFailureText(error: unknown): string {
  const { status } = (error ?? {}) as Partial<ApiError>;
  return status === 403 ? FORBIDDEN_MESSAGE : GENERIC_MESSAGE;
}

/**
 * What every write route answers with: the work row and the work status. A line decision
 * also carries the decision as stored.
 */
export type WorkAnswer = Pick<CaseWorkResult, "work" | "work_status"> & {
  decision?: LineDecision | null;
};

/** The cached case with the answer of a write route applied. */
export function applyWorkResult<
  T extends { work?: CaseWork; work_status?: string; lines?: { decision?: unknown }[] },
>(detail: T | undefined, result: WorkAnswer | undefined, lineIndex?: number): T | undefined {
  if (!detail || !result) {
    return detail;
  }
  const lines =
    lineIndex === undefined || !detail.lines
      ? detail.lines
      : detail.lines.map((line, index) =>
          index === lineIndex ? { ...line, decision: result.decision ?? null } : line,
        );
  return { ...detail, work: result.work, work_status: result.work_status, lines };
}

/** The fields of a cached line the optimistic writes read and change. */
export interface CachedLine {
  line_key?: string | null;
  decision?: unknown;
  item?: string | null;
  item_name?: string | null;
  qty?: number | null;
  /** "default": the mail gave no quantity and `qty` is the 1 I2Q used. */
  qty_source?: string | null;
  unit?: string | null;
  unit_price?: unknown;
  currency?: string | null;
  price_status?: string | null;
  price_source?: string | null;
  margin_status?: string | null;
  override?: unknown;
}

/** The slice of a cached case the work mutations read and write. */
export interface CachedCase {
  work?: CaseWork;
  work_status?: string;
  lines?: CachedLine[];
}

// ─── optimistic writes ──────────────────────────────────────────────────────
//
// What the cached case looks like the moment a person saves (ease round, 13 Oct 2026: a
// save showed nothing until the case was read again, 0.3 to 1.4 s later, and the same
// quantity was saved twice). Pure: the cached case in, a new one out. The server's answer
// and the next read of the case replace every value written here.

/** The stored correction of a cached line, when it has the shape of one. */
function overrideOf(line: CachedLine): Record<string, unknown> | null {
  const raw = line.override;
  return raw != null && typeof raw === "object" ? (raw as Record<string, unknown>) : null;
}

function withQuantity<L extends CachedLine>(line: L, qty: number, unit?: string): L {
  const before = overrideOf(line);
  // A quantity a person types is the person's: the default 1 mark goes (14 Oct 2026).
  const reviewed = { ...(unit === undefined ? {} : { unit }), qty_source: null };
  // A line sales added, or took out, keeps its own correction: only the figures change.
  if (before && before.kind !== "edit") {
    return { ...line, qty, ...reviewed };
  }
  return {
    ...line,
    qty,
    ...reviewed,
    override: {
      // What I2Q read stays next to the reviewed value, as the server stores it. The
      // default 1 is not what I2Q read: the mail gave no quantity.
      engine_qty: line.qty_source === "default" ? null : (line.qty ?? null),
      engine_unit: line.unit ?? null,
      engine_changed: false,
      decided_by: "",
      decided_at: "",
      ...before,
      kind: "edit",
      qty,
      ...(unit === undefined ? {} : { unit }),
    },
  };
}

/** The cached case with the quantity (and the unit when given) of one line set. */
export function optimisticQuantity<T extends CachedCase>(
  detail: T,
  lineIndex: number,
  qty: number,
  unit?: string,
): T {
  if (!detail.lines || !detail.lines[lineIndex]) {
    return detail;
  }
  return {
    ...detail,
    lines: detail.lines.map((line, index) =>
      index === lineIndex ? withQuantity(line, qty, unit) : line,
    ),
  };
}

/** The cached case with one quantity set on every line named by its key. */
export function optimisticQuantities<T extends CachedCase>(
  detail: T,
  lineKeys: readonly string[],
  qty: number,
  unit?: string,
): T {
  const keys = new Set(lineKeys);
  if (!detail.lines || keys.size === 0) {
    return detail;
  }
  return {
    ...detail,
    lines: detail.lines.map((line) =>
      line.line_key && keys.has(line.line_key) ? withQuantity(line, qty, unit) : line,
    ),
  };
}

/**
 * The cached case with a price a person set on one line. The margin check of the new
 * price is not known before the server answers, so the line reads "not checked".
 */
export function optimisticPrice<T extends CachedCase>(
  detail: T,
  lineIndex: number,
  unitPrice: string,
  currency: string,
): T {
  if (!detail.lines || !detail.lines[lineIndex]) {
    return detail;
  }
  return {
    ...detail,
    lines: detail.lines.map((line, index) => {
      if (index !== lineIndex) {
        return line;
      }
      const before = overrideOf(line);
      const manual = line.price_source === "sales";
      return {
        ...line,
        unit_price: unitPrice,
        currency,
        price_status: "found",
        price_source: "sales",
        margin_status: "not_checked",
        override: {
          kind: "edit",
          engine_changed: false,
          decided_by: "",
          decided_at: "",
          // The price I2Q found stays behind the person's price.
          engine_unit_price: manual ? null : (line.unit_price ?? null),
          engine_currency: manual ? null : (line.currency ?? null),
          ...before,
          unit_price: unitPrice,
          currency,
        },
      };
    }),
  };
}

/** The cached case with a decision on one line: confirm, an item, no item, or none. */
export function optimisticDecision<T extends CachedCase>(
  detail: T,
  lineIndex: number,
  kind: "confirm" | "item" | "no_item" | "clear",
  item?: string,
): T {
  const target = detail.lines?.[lineIndex];
  if (!detail.lines || !target) {
    return detail;
  }
  const lineKey = (target.line_key ?? "").trim();
  if (kind !== "clear" && lineKey === "") {
    return detail;
  }
  const decision =
    kind === "clear"
      ? null
      : {
          line_key: lineKey,
          kind,
          item:
            kind === "no_item" ? null : kind === "item" ? (item ?? null) : (target.item ?? null),
          item_area: null,
          item_name: kind === "confirm" ? (target.item_name ?? null) : null,
          decided_by: "",
          decided_at: "",
          engine_changed: false,
        };
  return {
    ...detail,
    lines: detail.lines.map((line, index) => (index === lineIndex ? { ...line, decision } : line)),
  };
}

// ─── mutations ──────────────────────────────────────────────────────────────

/** The mutation key of every write of one case. */
export const caseWriteKey = (caseId: string) => ["case-write", caseId];

/** What `onMutate` hands to `onError`: the cached case before an optimistic write. */
export interface WorkMutationContext {
  previous?: CachedCase;
}

export interface WorkMutationConfig<Args, Result extends WorkAnswer = CaseWorkResult> {
  queryClient: QueryClient;
  caseId: string;
  target: WorkTarget;
  /** Sends the request; `version` is the `expected_version` to put in the body. */
  send: (args: Args, version: number) => Promise<Result | undefined>;
  /** For a line decision: the index of the line the answer belongs to. */
  lineIndexOf?: (args: Args) => number;
  /** Raises the conflict toast. */
  notify: (message: string) => void;
  /**
   * The cached case as it reads the moment the person saves, before the server answers.
   * Without it nothing is shown before the answer.
   */
  optimistic?: (detail: CachedCase, args: Args) => CachedCase;
}

/**
 * The behaviour every case write shares, as TanStack mutation options:
 *   - the writes of one case run one after the other (`scope`), so five quantities typed
 *     in a row each carry the version the answer before brought, never a stale one;
 *   - `expected_version` is the version of the work row in the cached case (0 without one);
 *   - with `optimistic`, the cached case shows the new value at once; a failed write puts
 *     the lines back as they were and reads the case again;
 *   - on success the answer is applied to the cached case, then the case, its history
 *     and the queue are read again (prices, the order check and the state follow a
 *     decision). While another write of the case still waits, only the last one reads
 *     the case again, so an answer never paints over a value typed after it;
 *   - on 409 the conflict toast is raised, the current work row from the answer is put
 *     into the cache and the case is read again;
 *   - 422 and 503 raise nothing here: they stay on `mutation.error` for the control.
 */
export function workMutationOptions<Args, Result extends WorkAnswer = CaseWorkResult>(
  config: WorkMutationConfig<Args, Result>,
) {
  const { queryClient, caseId, target, send, lineIndexOf, notify, optimistic } = config;
  const caseKey = ["case", caseId];
  const writeKey = caseWriteKey(caseId);
  return {
    mutationKey: writeKey,
    scope: { id: writeKey.join(":") },
    mutationFn: async (args: Args) => {
      const shown = queryClient.getQueryData<CachedCase>(caseKey);
      return send(args, expectedVersion(shown?.work));
    },
    onMutate: async (args: Args): Promise<WorkMutationContext> => {
      if (!optimistic) {
        return {};
      }
      // A read of the case that is under way would paint the old value over the new one.
      await queryClient.cancelQueries({ queryKey: caseKey });
      const previous = queryClient.getQueryData<CachedCase>(caseKey);
      if (previous) {
        queryClient.setQueryData<CachedCase>(caseKey, optimistic(previous, args));
      }
      return { previous };
    },
    onSuccess: (result: Result | undefined, args: Args) => {
      queryClient.setQueryData<CachedCase>(caseKey, (detail) =>
        applyWorkResult(detail, result, lineIndexOf?.(args)),
      );
      // The write that runs this is still counted: more than one means others wait.
      if (queryClient.isMutating({ mutationKey: writeKey }) <= 1) {
        void queryClient.invalidateQueries({ queryKey: caseKey });
      }
      void queryClient.invalidateQueries({ queryKey: ["case-events", caseId] });
      void queryClient.invalidateQueries({ queryKey: ["queue"] });
    },
    onError: (error: unknown, _args?: Args, context?: WorkMutationContext) => {
      const previous = context?.previous;
      if (previous) {
        // The lines go back; the work row stays the newest one (an answer of an earlier
        // write may have raised its version since the snapshot was taken).
        queryClient.setQueryData<CachedCase>(caseKey, (detail) =>
          detail ? { ...detail, lines: previous.lines } : previous,
        );
      }
      const failure = workFailure(error, target);
      if (failure.kind !== "conflict") {
        if (previous) {
          void queryClient.invalidateQueries({ queryKey: caseKey });
        }
        return;
      }
      notify(failure.message);
      const work = failure.work;
      if (work) {
        queryClient.setQueryData<CachedCase>(caseKey, (detail) =>
          detail ? { ...detail, work } : detail,
        );
      }
      void queryClient.invalidateQueries({ queryKey: caseKey });
      void queryClient.invalidateQueries({ queryKey: ["queue"] });
    },
  };
}
